import assert from 'assert';
import { Device, Grid, Loading, Theme } from '@livecontrol/core-ui';
import { Enum, Str } from '@livecontrol/core-utils';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { useGetRouters, useScheduledEvent } from '../graphql';
import { Contacts } from './contacts';
import { Destinations } from './destinations';
import { EventDetailsSummary } from './event-details-summary';
import { OperatorNotes } from './operator-notes';
import { Performance } from './performance';
import { Routers } from './routers';
import { Player } from './spotlight';
import { Tab, Tabber } from './tabber';
import { VideographerInfo } from './videographer-info';
import { Webplayer } from './webplayer';

export const EventDetailsLayout = (): React.ReactElement => {
  const viewport = Theme.useViewport();
  const isDesktop = Device.isDesktop(viewport);
  const eid = Str.normalize(useParams<{ eid: string }>().eid);
  const [tab, setTab] = useState<Tab>(Tab.OperatorNotes);
  const [showPlayer, setShowPlayer] = useState(true);

  const togglePlayer = useCallback(() => {
    setShowPlayer(prev => !prev);
  }, []);

  assert(typeof eid === 'string');

  const { loading, scheduledEvent, refetch } = useScheduledEvent(eid);

  const handleRefresh = async (): Promise<void> => {
  try {
    await refetch();
  } catch (error: unknown) {
    console.error('Error on data refresh:', error);
  }
};

  // console.log("scheduledEvent: ", scheduledEvent);

  const customerId = scheduledEvent?.location?.user?.id;

  const { loading: loadingRouters, routers } = useGetRouters(Number(scheduledEvent?.location?.user?.id),{
  skip: !customerId
});

  const TabContentMap = {
    [Tab.Destinations]: scheduledEvent && <Destinations scheduledEvent={scheduledEvent} />,
    [Tab.Performance]: <Performance scheduledEvent={scheduledEvent} />,
    [Tab.OperatorNotes]: scheduledEvent && <OperatorNotes scheduledEvent={scheduledEvent} />,
    [Tab.Contacts]: <Contacts scheduledEvent={scheduledEvent} />,
    [Tab.Routers]: loadingRouters
    ? <Loading isOldVersion />
    : <Routers scheduledEvent={scheduledEvent} routersData={routers} />,
    [Tab.Webplayer]: scheduledEvent && <Webplayer scheduledEvent={scheduledEvent} />,
    [Tab.VideographerInfo]: scheduledEvent && <VideographerInfo scheduledEvent={scheduledEvent} />
  };

  const tabContent = TabContentMap[tab];

  return loading ? (
    <Loading isOldVersion />
  ) : scheduledEvent ? (
    <Grid
      gridTemplateColumns={isDesktop ? '375px 1fr' : '1fr'}
      gridTemplateAreas={
        isDesktop
          ? `
          "spotlight details"
          "tabs tabs"
        `
          : `
          "spotlight"
          "details"
          "tabs"
        `
      }
      columnGap='3rem'
      rowGap='1rem'
    >
      <Grid.Item gridArea='spotlight'>
        {showPlayer && (
        <Player scheduledEvent={scheduledEvent} />
        )}
      </Grid.Item>
      <Grid.Item gridArea='details'>
        <EventDetailsSummary scheduledEvent={scheduledEvent} togglePlayer={togglePlayer} handleRefresh={handleRefresh}/>
      </Grid.Item>
      <Grid.Item gridArea='tabs'>
        <Tabber
          eventType={scheduledEvent.event_type}
          tab={tab}
          onSelect={(key): void => {
            setTab(Enum.normalize(Tab, key)!);
          }}
        />
        {tabContent}
      </Grid.Item>
    </Grid>
  ) : (
    <div className='text-white'>Could not load event details</div>
  );
};
